<template>
  <div class="overall-report-container">
    <h2 class="main-title">
      <i class="el-icon-s-data mr-2"></i>
      Overall Report
    </h2>

    <div v-if="report" class="report-dashboard">
      <div class="row mb-4 align-items-end">
        <div class="col-auto">
          <span>Select Date(s)</span>
          <br />
          <el-select v-model="dateFilterOption">
            <el-option :value="0" :label="'All'">All</el-option>
            <el-option :value="1" :label="'Custom Date'">Custom Date</el-option>
          </el-select>
        </div>
        <div v-if="dateFilterOption" class="col-auto">
          <span>Select Date Range</span>
          <v-md-date-range-picker
            :presets="dateRangePresets"
            :start-date="dateRange.start_date"
            :end-date="dateRange.end_date"
            @change="handleChange"
            style="width:100% !important"
          ></v-md-date-range-picker>
        </div>
        <div class="col mt-2">
          <el-button type="primary" @click="getSurgeonReport"
            >Get Report</el-button
          >
        </div>
      </div>

      <div class="summary-section d-flex flex-wrap justify-content-between">
        <div
          v-for="(item, index) in summaryCards"
          :key="index"
          class="flex-fill"
        >
          <el-card shadow="hover" class="summary-card" :class="item.color">
            <div class="summary-top">
              <i :class="item.icon"></i>
              <span class="summary-title">{{ item.title }}</span>
            </div>
            <div class="summary-value">{{ item.value }}</div>
          </el-card>
        </div>
      </div>

      <el-card shadow="hover" class="section-card">
        <div class="section-header">
          <h3>Surgeon Performance</h3>
        </div>
        <div class="surgeon-grid">
          <div v-if="!report.surgical_details.surgeons.length">
            No surgeon data found.
          </div>
          <div
            v-for="(surgeon, index) in report.surgical_details.surgeons"
            :key="index"
            class="surgeon-item"
          >
            <div class="surgeon-avatar">
              <el-avatar
                :size="50"
                :style="{ backgroundColor: getColor(surgeon.surgeon_name) }"
              >
                {{ getInitials(surgeon.surgeon_name) }}
              </el-avatar>
            </div>
            <div class="surgeon-info">
              <h4>{{ surgeon.surgeon_name || "Unnamed" }}</h4>
              <p>{{ surgeon.total }} Surgeries</p>
            </div>
          </div>
        </div>
      </el-card>

      <el-card shadow="hover" class="section-card">
        <div class="section-header mb-3">
          <h3>Post-Operative Release Summary</h3>
        </div>

        <div class="mb-4">
          <h6 class="text-muted mb-3 border-bottom pb-2">
            Operated & Released Details
          </h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in postOpStatsGroup1"
              :key="key"
            >
              <div class="stat-item text-center py-3 h-100">
                <h2 class="mb-1">{{ value }}</h2>
                <p class="mb-0 small text-muted">{{ key }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <h6 class="text-muted mb-3 border-bottom pb-2">
            Escaped / Claimed Summary
          </h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in postOpStatsGroup2"
              :key="key"
            >
              <div class="stat-item text-center py-3 h-100">
                <h2 class="mb-1">{{ value }}</h2>
                <p class="mb-0 small text-muted">{{ key }}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h6 class="text-muted mb-3 border-bottom pb-2">Total Summary</h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in postOpStatsTotals"
              :key="key"
            >
              <div
                class="stat-item text-center py-3 h-100"
              >
                <h2 class="mb-1 text-primary">{{ value }}</h2>
                <p class="mb-0 small fw-bold">{{ key }}</p>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card shadow="hover" class="section-card catching-section">
        <div class="section-header">
          <h3>Catching Method Breakdown</h3>
        </div>
        <div class="row">
          <div
            class="col-md-2 col-sm-6 my-2"
            v-for="(count, method) in report.catching_method_breakdown"
            :key="method"
          >
            <div class="stat-item text-center py-3 h-100">
                <h2 class="mb-1">{{ count }}</h2>
                <p class="mb-0 small text-muted text-capitalize">{{ method.replace("_", " ") }}</p>
              </div>
          </div>
        </div>
      </el-card>

      <el-card shadow="hover" class="section-card">
        <div class="section-header mb-3">
          <h3>Demographics Summary</h3>
        </div>

        <div class="mb-4">
          <h6 class="text-muted mb-3 border-bottom pb-2">
            Gender Distribution
          </h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in demographicsGender"
              :key="key"
            >
              <div class="stat-item text-center py-3 h-100">
                <h2 class="mb-1">{{ value }}</h2>
                <p class="mb-0 small text-muted text-capitalize">{{ key }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <h6 class="text-muted mb-3 border-bottom pb-2">Age Distribution</h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in demographicsAge"
              :key="key"
            >
              <div class="stat-item text-center py-3 h-100">
                <h2 class="mb-1 text-success">{{ value }}</h2>
                <p class="mb-0 small text-muted text-capitalize">{{ key }}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h6 class="text-muted mb-3 border-bottom pb-2">
            Female-Specific Stats
          </h6>
          <div class="row g-3">
            <div
              class="col-md-4 col-sm-6"
              v-for="(value, key) in demographicsFemaleSpecific"
              :key="key"
            >
              <div
                class="stat-item text-center p-3 h-100"
              >
                <h2 class="mb-1 text-pink">{{ value }}</h2>
                <p class="mb-0 small fw-bold text-capitalize">
                  {{ formatLabel(key) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card shadow="hover" class="section-card">
        <div class="section-header">
          <h3>Mortality Statistics</h3>
        </div>
        <div class="row mortality-grid">
          <div
            class="col-md-4 col-sm-12"
            v-for="(value, key) in report.mortality"
            :key="key"
          >
            <div class="mortality-item">
              <h2>{{ value }}</h2>
              <p>{{ formatKey(key) }}</p>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import {
  Card,
  Button,
  Divider,
  Message,
  Row,
  Col,
  Avatar,
  DatePicker,
  Select,
} from "element-ui";
import VMdDateRangePicker from "v-md-date-range-picker";
import apiService from "../../../../services/api.service";
import dateRangeDefaultPresetsMixin from "../../../../mixins/DateRangeDefaultPresetsMixin";
import mixin from "../../../../mixins/GlobalMixin";
import Vue from "vue";

Vue.use(VMdDateRangePicker);

export default {
  components: {
    [Card.name]: Card,
    [Button.name]: Button,
    [Divider.name]: Divider,
    [Row.name]: Row,
    [Col.name]: Col,
    [Avatar.name]: Avatar,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      report: null,
      dateRange: {
        start_date: firstDay.toLocaleDateString("en-US"),
        end_date: lastDay.toLocaleDateString("en-US"),
      },
      dateFilterOption: 0,
      dateRangePresets: [],
    };
  },
  mixins: [mixin, dateRangeDefaultPresetsMixin],
  computed: {
    summaryCards() {
      const d = this.report.surgical_details;
      if (!d) return [];
      return [
        {
          title: "Total Surgeries",
          value: d.total_surgeries,
          color: "blue",
          icon: "el-icon-s-data",
        },
        {
          title: "Avg Time (mins)",
          value: d.average_time_mins,
          color: "green",
          icon: "el-icon-time",
        },
        {
          title: "Spay (Female)",
          value: d.spay_female,
          color: "pink",
          icon: "el-icon-female",
        },
        {
          title: "Castration (Male)",
          value: d.castration_male,
          color: "purple",
          icon: "el-icon-male",
        },
      ];
    },
    postOpStats() {
      return {
        "Total Operated & Released": this.report.post_op_release_summary
          .total_operated_and_released,
        "Not Operated but Released": this.report.post_op_release_summary
          .total_not_operated_but_released,
        "Total Released": this.report.post_op_release_summary.total_released,
      };
    },
    postOpStatsGroup1() {
      const s = this.report.post_op_release_summary || {};
      return {
        "Post-Op Animals": s.post_op_animals,
        "Already Operated": s.already_operated,
        "Already Vaccinated (ARV)": s.already_vaccinated_arv,
      };
    },
    postOpStatsGroup2() {
      const s = this.report.post_op_release_summary || {};
      return {
        "Pre-Op Escaped": s.pre_op_escaped,
        "Post-Op Escaped": s.post_op_escaped,
        "Claimed by Owner": s.claimed_by_owner,
      };
    },
    postOpStatsTotals() {
      const s = this.report.post_op_release_summary || {};
      return {
        "Total Operated & Released": s.total_operated_and_released,
        "Not Operated but Released": s.total_not_operated_but_released,
        "Total Released": s.total_released,
      };
    },
    demographicsGender() {
      const d = this.report.demographics || {};
      return {
        male: d.male,
        female: d.female,
      };
    },
    demographicsAge() {
      const d = this.report.demographics || {};
      return {
        adult: d.adult,
        young: d.young,
        pup: d.pup,
      };
    },
    demographicsFemaleSpecific() {
      const d = this.report.demographics || {};
      return {
        "lactating females": d.lactating_females,
        "pregnant females": d.pregnant_females,
      };
    },
  },
  methods: {
    async getSurgeonReport() {
      const vm = this;
      const loading = vm.$loading({
        lock: true,
        text: "Loading statistics...",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.7)",
      });
      try {
        const payload = this.dateFilterOption ? this.dateRange : {};
        const apiResponse = await apiService.getOverallReport(
          vm.projectId,
          payload
        );
        const { response } = apiResponse;
        if (response.error && response.error.error_code === 0) {
          this.report = response.data.report;
        } else {
          Message.error("Failed to load surgeon statistics");
        }
      } catch (error) {
        console.error("Error fetching surgeon statistics:", error);
        Message.error("An error occurred while fetching surgeon statistics");
      } finally {
        loading.close();
      }
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = name.split(" ");
      return parts.length > 1
        ? parts[0][0] + parts[1][0]
        : name.slice(0, 2).toUpperCase();
    },
    getColor(name) {
      const colors = ["#42b983", "#e67e22", "#3498db", "#9b59b6", "#2ecc71"];
      const index = name ? name.length % colors.length : 0;
      return colors[index];
    },
    formatKey(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    handleChange(values) {
      this.dateRange.start_date = this.formatDate(values[0]._d);
      this.dateRange.end_date = this.formatDate(values[1]._d);
    },
    formatDate(date) {
      return new Date(date).toISOString().split("T")[0];
    },
    getGenderColor(key) {
      if (key === "male") return "text-primary";
      if (key === "female") return "text-pink";
      return "text-dark";
    },
    formatLabel(label) {
      return label.replace("_", " ");
    },
  },
  mounted() {
    this.getSurgeonReport();
    this.dateRangePresets = this.getDateRangePresets();
  },
};
</script>

<style scoped>
.report-dashboard {
  padding: 10px 30px;
  min-height: 100vh;
}
h6 {
  text-transform: none;
}
.summary-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  border-radius: 12px;
  min-height: 100px;
}

.summary-top {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.85;
}

.summary-top i {
  font-size: 20px;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  margin-top: 6px;
}

.summary-card.blue {
  background: linear-gradient(135deg, #2196f3, #64b5f6);
}

.summary-card.green {
  background: linear-gradient(135deg, #43a047, #81c784);
}

.summary-card.pink {
  background: linear-gradient(135deg, #ec407a, #f48fb1);
}

.summary-card.purple {
  background: linear-gradient(135deg, #8e24aa, #ba68c8);
}

/* Surgeon section */
.surgeon-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 10px;
}

.surgeon-item {
  flex: 1 1 calc(25% - 10px);
  display: flex;
  align-items: center;
  background: #fff;
  padding: 10px 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: 0.3s;
}

.surgeon-item:hover {
  transform: translateY(-3px);
}

.surgeon-info {
  margin-left: 10px;
}

.surgeon-info h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.surgeon-info p {
  margin: 0;
  font-size: 13px;
  color: #777;
}

/* Post-op */
.stats-grid .stat-item {
  text-align: center;
  padding: 20px;
  background: #fff;
  border-radius: 10px;
}

.stat-item h2 {
  color: #409eff;
  margin-bottom: 5px;
}

.stat-item p {
  font-size: 14px;
  color: #666;
}

/* === Catching Method Section === */
.catching-section .stat-item {
  background: #fafafa;
}

.catching-section .stat-item h2 {
  color: #43a047;
}

.catching-section .stat-item p {
  font-size: 13px;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.3;
}

/* Mortality */
.mortality-item {
  background: #fff;
  text-align: center;
  border-radius: 10px;
  padding: 20px;
}

.mortality-item h2 {
  color: #e74c3c;
  margin-bottom: 5px;
}

/* Section cards */
.section-card {
  margin-top: 25px;
  border-radius: 12px;
}

.section-header {
  margin-bottom: 18px;
  display: flex;
  align-items: center;
}

.section-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* ---------- RESPONSIVE DESIGN ---------- */

@media screen and (max-width: 768px) {
  .report-dashboard {
    padding: 10px 15px;
  }

  .summary-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .summary-card {
    width: 100%;
    min-height: 80px;
  }

  .surgeon-grid {
    flex-direction: column;
  }
  .surgeon-item {
    flex: 1 1 100%;
  }

  .section-card {
    padding: 10px;
  }

  .section-header h3 {
    font-size: 16px;
  }
}

@media screen and (max-width: 480px) {
  .report-title {
    font-size: 18px;
    text-align: center;
  }

  .summary-value {
    font-size: 18px;
  }

  .summary-top {
    font-size: 12px;
  }

  .surgeon-item {
    padding: 8px 10px;
  }

  .stat-item h2 {
    font-size: 18px;
  }
}
</style>
